import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Linkedin, Twitter, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { companyInfo } from '../../data/company';
import { services } from '../../data/services';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="bg-foreground text-background/90">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-12 w-auto brightness-0 invert"
            />
            <p className="text-background/70 text-sm leading-relaxed">
              {companyInfo.tagline}
            </p>
            <div className="flex gap-4">
              <a
                href={`https://facebook.com/${companyInfo.contact.social.facebook}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-background/10 hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={`https://linkedin.com/${companyInfo.contact.social.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-background/10 hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href={`https://twitter.com/${companyInfo.contact.social.twitter.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-background/10 hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-background/70 hover:text-accent transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-background/70 hover:text-accent transition-colors text-sm">
                  About
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-background/70 hover:text-accent transition-colors text-sm">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-background/70 hover:text-accent transition-colors text-sm">
                  Products
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-background/70 hover:text-accent transition-colors text-sm">
                  Resources
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-background/70 hover:text-accent transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.slice(0, 6).map((service) => (
                <li key={service.id}>
                  <Link
                    to={service.route || `/services#${service.id}`}
                    className="text-background/70 hover:text-accent transition-colors text-sm"
                  >
                    {service.name.length > 30 ? service.name.substring(0, 30) + '...' : service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - EXACT */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <div className="text-background/70 text-sm">
                  {companyInfo.contact.address.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <div className="text-background/70 text-sm">
                  {companyInfo.contact.phones.map((phone, i) => (
                    <p key={i}>{phone}</p>
                  ))}
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-accent shrink-0" />
                <a
                  href={`mailto:${companyInfo.contact.email}`}
                  className="text-background/70 hover:text-accent transition-colors text-sm"
                >
                  {companyInfo.contact.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <ExternalLink className="w-5 h-5 text-accent shrink-0" />
                <a
                  href={`https://${companyInfo.contact.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-background/70 hover:text-accent transition-colors text-sm"
                >
                  {companyInfo.contact.website}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-background/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-background/50 text-sm">
              © {currentYear} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/contact" className="text-background/50 hover:text-accent text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/contact" className="text-background/50 hover:text-accent text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
